import {
  classifyBatchApplyOutcome,
  ermittleEffektivenBatchAufschlag,
  formatBatchAufschlagHinweis,
  offerIdentityKey,
  type BatchApplyDisposition,
} from './catalog-batch-suggest';
import type { KalkulationsDefaults } from './kalkulation';
import type { PositionsKalkulation } from './schema';

export type BatchApplyToastKind = 'success' | 'partial' | 'rejected';

interface BatchApplyUiSummary {
  disposition: BatchApplyDisposition;
  visiblePositionIds: number[];
  appliedPositionIds: number[];
  rejectedPositionIds: number[];
  pendingPositionIds: number[];
  shouldCloseDialog: boolean;
  toastKind: BatchApplyToastKind;
  appliedCount: number;
  rejectedCount: number;
  applyButtonCount: number;
}

export interface CatalogBatchApplyTransition extends BatchApplyUiSummary {
  checkedPositionIds: number[];
}

export function transitionCatalogBatchApply(input: {
  visiblePositionIds: readonly number[];
  checkedPositionIds: readonly number[];
  attemptedPositionIds: readonly number[];
  applyResults: readonly boolean[];
}): CatalogBatchApplyTransition {
  const outcome = classifyBatchApplyOutcome(
    input.attemptedPositionIds,
    input.applyResults,
  );
  const applied = new Set(outcome.appliedPositionIds);
  const pendingPositionIds = outcome.shouldCloseDialog
    ? []
    : [...outcome.rejectedPositionIds];

  return {
    disposition: outcome.disposition,
    visiblePositionIds: input.visiblePositionIds.filter((id) => !applied.has(id)),
    checkedPositionIds:
      outcome.disposition === 'vollstaendig-abgelehnt'
        ? [...input.checkedPositionIds]
        : [...pendingPositionIds],
    appliedPositionIds: [...outcome.appliedPositionIds],
    rejectedPositionIds: [...outcome.rejectedPositionIds],
    pendingPositionIds,
    shouldCloseDialog: outcome.shouldCloseDialog,
    toastKind:
      outcome.disposition === 'vollstaendig-erfolgreich'
        ? 'success'
        : outcome.disposition === 'teilweise-erfolgreich'
          ? 'partial'
          : 'rejected',
    appliedCount: outcome.appliedPositionIds.length,
    rejectedCount: outcome.rejectedPositionIds.length,
    applyButtonCount: pendingPositionIds.length,
  };
}

export function planX84MaterialEkAttempt(input: {
  matchPositionIds: readonly number[];
  blockedPositionIds: readonly number[];
  appliedPositionIds: readonly number[];
}): number[] {
  const blocked = new Set(input.blockedPositionIds);
  const applied = new Set(input.appliedPositionIds);
  return input.matchPositionIds.filter(
    (positionId) => !blocked.has(positionId) && !applied.has(positionId),
  );
}

export interface X84MaterialEkApplyTransition extends BatchApplyUiSummary {
  blockedPositionIds: number[];
}

export function transitionX84MaterialEkApply(input: {
  matchPositionIds: readonly number[];
  blockedPositionIds: readonly number[];
  previouslyAppliedPositionIds: readonly number[];
  attemptedPositionIds: readonly number[];
  applyResults: readonly boolean[];
}): X84MaterialEkApplyTransition {
  const outcome = classifyBatchApplyOutcome(
    input.attemptedPositionIds,
    input.applyResults,
  );
  const applied = new Set(input.previouslyAppliedPositionIds);
  for (const positionId of outcome.appliedPositionIds) {
    applied.add(positionId);
  }
  const appliedPositionIds = input.matchPositionIds.filter((id) => applied.has(id));
  const blockedPositionIds = input.matchPositionIds.filter((id) =>
    input.blockedPositionIds.includes(id),
  );
  const pendingPositionIds = outcome.shouldCloseDialog
    ? []
    : planX84MaterialEkAttempt({
        matchPositionIds: input.matchPositionIds,
        blockedPositionIds,
        appliedPositionIds,
      });

  return {
    disposition: outcome.disposition,
    visiblePositionIds: [...input.matchPositionIds],
    blockedPositionIds,
    appliedPositionIds,
    rejectedPositionIds: [...outcome.rejectedPositionIds],
    pendingPositionIds,
    shouldCloseDialog: outcome.shouldCloseDialog,
    toastKind:
      outcome.disposition === 'vollstaendig-erfolgreich'
        ? 'success'
        : outcome.disposition === 'teilweise-erfolgreich'
          ? 'partial'
          : 'rejected',
    appliedCount: outcome.appliedPositionIds.length,
    rejectedCount: outcome.rejectedPositionIds.length,
    applyButtonCount: pendingPositionIds.length,
  };
}

export interface X84OfferIdentityTransition {
  identityChanged: boolean;
  nextGeneration: number;
  shouldCloseDialog: boolean;
  shouldResetImport: boolean;
}

export function transitionX84OfferIdentity(input: {
  previousIdentity: string;
  nextIdentity: string;
  currentGeneration: number;
}): X84OfferIdentityTransition {
  const identityChanged = input.previousIdentity !== input.nextIdentity;
  return {
    identityChanged,
    nextGeneration: identityChanged
      ? input.currentGeneration + 1
      : input.currentGeneration,
    shouldCloseDialog: identityChanged,
    shouldResetImport: identityChanged,
  };
}

export function canCommitX84ParseResult(input: {
  startGeneration: number;
  currentGeneration: number;
  startOfferIdentity?: string;
  startOfferDocumentEpoch?: number;
  getCurrentOfferState: () => {
    currentDatabaseId: string | null | undefined;
    currentQuoteSlot: number | null | undefined;
    offerDocumentEpoch?: number;
  };
}): boolean {
  const currentOfferState = input.getCurrentOfferState();
  const currentOfferIdentity = offerIdentityKey(
    currentOfferState.currentDatabaseId,
    currentOfferState.currentQuoteSlot,
  );
  return (
    input.startGeneration === input.currentGeneration &&
    (
      input.startOfferIdentity === undefined ||
      input.startOfferIdentity === currentOfferIdentity
    ) &&
    (
      input.startOfferDocumentEpoch === undefined ||
      input.startOfferDocumentEpoch === currentOfferState.offerDocumentEpoch
    )
  );
}

export function canCommitOfferDocumentTask(input: {
  startGeneration: number;
  currentGeneration: number;
  expectedDocumentEpoch: number;
  currentDocumentEpoch: number;
  expectedPositionId?: number;
  currentPositionId?: number;
}): boolean {
  return (
    input.startGeneration === input.currentGeneration &&
    input.expectedDocumentEpoch === input.currentDocumentEpoch &&
    (
      input.expectedPositionId === undefined ||
      input.expectedPositionId === input.currentPositionId
    )
  );
}

export function commitOfferDocumentWriteIfCurrent<T>(input: {
  expectedDocumentEpoch: number;
  getCurrentDocumentEpoch: () => number;
  write: () => T;
}): { committed: false } | { committed: true; value: T } {
  if (input.getCurrentDocumentEpoch() !== input.expectedDocumentEpoch) {
    return { committed: false };
  }
  return { committed: true, value: input.write() };
}

export interface X84DirectApplyResult {
  appliedCount: number;
  skippedCount: number;
  documentChanged: boolean;
}

export function applyX84DirectUnitPrices(input: {
  matches: readonly { positionId: number; unitPrice: number }[];
  expectedDocumentEpoch: number;
  getCurrentDocumentEpoch: () => number;
  hasPosition: (positionId: number) => boolean;
  updatePosition: (positionId: number, unitPrice: number) => void;
}): X84DirectApplyResult {
  let appliedCount = 0;
  let skippedCount = 0;

  for (const match of input.matches) {
    if (!input.hasPosition(match.positionId)) {
      skippedCount += 1;
      continue;
    }
    const write = commitOfferDocumentWriteIfCurrent({
      expectedDocumentEpoch: input.expectedDocumentEpoch,
      getCurrentDocumentEpoch: input.getCurrentDocumentEpoch,
      write: () => input.updatePosition(match.positionId, match.unitPrice),
    });
    if (!write.committed) {
      return { appliedCount, skippedCount, documentChanged: true };
    }
    appliedCount += 1;
  }

  return { appliedCount, skippedCount, documentChanged: false };
}

export function formatX84DirectApplyDescription(result: X84DirectApplyResult): string {
  const skippedNotice = result.skippedCount > 0
    ? ` ${result.skippedCount} Position(en) übersprungen (siehe Hinweise in der Tabelle).`
    : '';
  return `${result.appliedCount} Einheitspreis(e) direkt übernommen.${skippedNotice}`;
}

export function selectSuccessfullyAppliedValues<T>(
  values: readonly T[],
  applyResults: readonly boolean[],
): T[] {
  if (values.length !== applyResults.length) {
    throw new Error('values und applyResults müssen gleich lang sein');
  }
  return values.filter((_, index) => applyResults[index] === true);
}

export function formatX84MaterialApplyDescription(
  appliedCount: number,
  successfullyAppliedCalculations: readonly PositionsKalkulation[],
  defaults: KalkulationsDefaults,
  outcomeCounts: {
    rejectedCount?: number;
    blockedCount?: number;
  } = {},
): string {
  const surchargeNotice = formatBatchAufschlagHinweis(
    ermittleEffektivenBatchAufschlag(successfullyAppliedCalculations, defaults),
  );
  const surchargeSuffix = surchargeNotice ? ` (${surchargeNotice})` : '';
  const rejectedNotice = (outcomeCounts.rejectedCount ?? 0) > 0
    ? ` ${outcomeCounts.rejectedCount} Position(en) abgelehnt — bitte einzeln prüfen.`
    : '';
  const blockedNotice = (outcomeCounts.blockedCount ?? 0) > 0
    ? ` ${outcomeCounts.blockedCount} Position(en) übersprungen (siehe Hinweise in der Tabelle).`
    : '';
  return (
    `${appliedCount} Position(en) mit Material-EK aktualisiert${surchargeSuffix}.` +
    rejectedNotice +
    blockedNotice
  );
}
